import { useCallback, useMemo } from 'react';

import styled from '@emotion/styled';
import { selectClasses, useMediaQuery } from '@kc-console/common-ui';
import {
  QUERY_KEY,
  TOKEN_SCOPE,
  useScopedLinkBuilder,
  useServiceAppState,
} from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import IcLocationRegular from '@kep/kcds-icons/IcLocationRegular';

import { MediaQuery } from '../../../constants';
import { useRefinedRegionMenuState } from '../../../hooks';
import GnbSelect from './GnbSelect';

import type { SelectOption } from '@kc-console/common-ui';

const RegionContainer = styled.div`
  display: flex;
  min-width: 120px;

  .${selectClasses.root}.Mui-disabled & {
    color: ${({ theme }) => theme.color['text/global/disabled']};
    -webkit-text-fill-color: ${({ theme }) => theme.color['text/global/disabled']};
  }
`;

const RegionMenu = () => {
  const { t } = useTranslation();
  const { getProps } = useScopedLinkBuilder();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const { state } = useServiceAppState();
  const { scope, serviceKeyword } = state;
  const { currentRegion: regionName, accessibleRegions } = useRefinedRegionMenuState();

  const isGlobal = useMemo(() => scope !== TOKEN_SCOPE.PROJECT, [scope]);
  const currentRegionName = useMemo(
    () => (isGlobal ? 'Global' : regionName),
    [isGlobal, regionName],
  );
  const tooltipMessage = useMemo(
    () => t('layout:gnb.region.current_usage', { regionName: currentRegionName }),
    [currentRegionName, t],
  );
  const options: SelectOption<string>[] = useMemo(() => {
    return [
      {
        title: t('layout:gnb.region.label'),
        type: 'section',
        items: accessibleRegions?.map(region => {
          return {
            text: region || '',
            value: region || '',
          };
        }),
      },
    ];
  }, [accessibleRegions, t]);

  const handleRegionChange = useCallback(
    (region: unknown) => {
      if (typeof region !== 'string') return;
      // TODO(navigation): Select anchor 지원 시 isAnchor:false 제거 → href spread
      getProps(serviceKeyword, {
        forceFullPageReload: true,
        isAnchor: false,
        searchParams: {
          [QUERY_KEY.REGION]: region,
          ...(regionName !== region ? { change_region: 'true' } : undefined),
        },
      }).onClick();
    },
    [getProps, regionName, serviceKeyword],
  );

  return (
    <div>
      <RegionSelect
        block
        aria-label={t('layout:gnb.region.select')}
        options={options}
        value={currentRegionName}
        disabled={isGlobal}
        renderValue={() =>
          !isMobile && <RegionContainer>{currentRegionName || ''}</RegionContainer>
        }
        renderTooltip={() => (currentRegionName ? tooltipMessage : '')}
        placeholder={!currentRegionName ? t('layout:gnb.region.select') : ''}
        onChange={handleRegionChange}
        startAdornment={<IcLocationRegular />}
      />
    </div>
  );
};

export default RegionMenu;

const RegionSelect = styled(GnbSelect)`
  ${MediaQuery.TIL_SMALL} {
    width: 64px;
  }
`;
